/**
 * Pure helper functions for CSV import logic.
 *
 * Extracted from useCsvImport.ts — these have no React dependencies and can be
 * tested and reused independently.
 */

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { AssociationMatcher } from '@/lib/crm/hooks/useAssociationMatchers';
import type {
  AssociationMapping,
  ColumnMapping,
  ColumnMappings,
  CrmEntityAssociationMapping,
  CsvImport,
  EntityTypeMappingSuggestion,
  FixedValueMapping,
  MultiTypeMappingSuggestion,
  NewPropertySuggestion,
  StageMappings,
  UploadedFileEntry,
} from '@/lib/csv-import/api';
import { getFileResults } from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

// Unique identifier properties preferred for join keys (in priority order)
const UNIQUE_IDENTIFIER_PROPERTIES = ['ahoy_email', 'ahoy_domain', 'ahoy_name'];

// ============================================================================
// Within-file association selections (directed: source = above, target = below)
// ============================================================================

/**
 * Per-file, per-directed-pair association-type selections.
 *
 *   fileId -> `${sourceType}|${targetType}` -> associationTypeName
 *
 * Value semantics for a directed (source-above → target-below) pair:
 *   - a non-empty `associationTypeName`  → link with that explicit type
 *   - `''` (empty string)                → user disabled the link ("Don't link")
 *   - absent                             → default to the first valid type
 */
export type AssociationTypeSelections = Record<
  string | number,
  Record<string, string>
>;

/** Sentinel stored when the user explicitly chooses NOT to link a pair. */
export const ASSOCIATION_DISABLED = '';

/** Directed pair key: source (above) and target (below) entity-type names. */
export function directedPairKey(
  sourceType: string,
  targetType: string
): string {
  return `${sourceType}|${targetType}`;
}

/**
 * Valid association types for a directed pair (source = above, target = below),
 * factoring in entity kinds via the provided matchers. Returns the association
 * types whose source side matches `sourceType` AND target side matches
 * `targetType`.
 */
export function validAssociationTypesForPair(
  associationTypes: AssociationType[],
  matchesSource: AssociationMatcher,
  matchesTarget: AssociationMatcher,
  sourceType: string,
  targetType: string
): AssociationType[] {
  return associationTypes.filter(
    at => matchesSource(at, sourceType) && matchesTarget(at, targetType)
  );
}

/**
 * Resolve the association type to use for a directed pair, given the user's
 * selections and the valid types. Returns the chosen `association_type_name`,
 * or `null` when the pair is disabled or has no valid association type.
 */
export function resolveSelectedAssociationType(
  fileSelections: Record<string, string>,
  sourceType: string,
  targetType: string,
  validTypes: AssociationType[]
): string | null {
  const selection = fileSelections[directedPairKey(sourceType, targetType)];
  // Explicit '' means the user disabled the link.
  if (selection === ASSOCIATION_DISABLED && selection !== undefined) {
    return null;
  }
  // A non-empty explicit choice wins only if it is still valid for this directed
  // pair (the persisted selection can go stale when the source/target kinds
  // change); otherwise default to the first valid type.
  return validTypes.some(t => t.association_type_name === selection)
    ? selection
    : (validTypes[0]?.association_type_name ?? null);
}

/**
 * Sentinel value stored in `columnMappings` when a user explicitly skips a
 * CSV column. Distinguished from missing entries so the UI can mark the row
 * as deliberately ignored versus simply not yet mapped. Stripped before the
 * payload is sent to the backend.
 */
export const SKIP_MARKER = '__skip__';

/**
 * The column mappings of a section that actually reach the payload.
 *
 * Excludes:
 *  - skipped (SKIP_MARKER) and present-but-unmapped ('') columns — a column
 *    dragged into a type starts as '' until the user maps it;
 *  - internal (system-managed) columns, which are never importable and must
 *    reach the backend only via the read-only internal_columns list. Filtering
 *    them here stops a stale or resumed mapping override from serializing one.
 *
 * `validateMappings` and `processMultiTypeFile` both derive from this single
 * helper. Keeping them on one filter is what stops the Submit gate from passing
 * on a mapping the payload builder would drop — which would send an empty
 * import the backend rejects, after the user already passed validation.
 */
function importableMappingEntries(
  rawMappings: ColumnMapping,
  internalColumns: string[] | undefined
): [string, string][] {
  const internalColumnSet = new Set(internalColumns ?? []);
  return Object.entries(rawMappings).filter(
    ([column, value]) =>
      value !== SKIP_MARKER && value !== '' && !internalColumnSet.has(column)
  );
}

/**
 * The droppable id for the "Unassigned" fallback bucket of a file: columns that
 * belong to no entity-type section. Every CSV column is always visible exactly
 * once — in a section, or here. Keyed per file so multiple uploads don't collide.
 */
export function unassignedVirtualKey(fileId: string | number): string {
  return `${fileId}_unassigned`;
}

/**
 * Move a CSV column from one entity-type "virtual file" to another in a
 * multi-type import (MOVE semantics: the column leaves the source type and
 * appears only in the target type). Returns a new `columnMappings`.
 *
 * The `unassignedKey` (see `unassignedVirtualKey`) is the fallback bucket. It
 * has no stored map — a column is "unassigned" purely by being absent from every
 * section's effective map. So:
 *   - target === unassignedKey → drop the column from its source section only
 *     (it then surfaces in the Unassigned row).
 *   - source === unassignedKey → add the column to the target section only
 *     (there is no source map to edit).
 *
 * The source/target effective maps are materialized from the user override
 * (`columnMappings[vk]`) or, if absent, the AI suggestion. The column carries the
 * target type's own suggested property if it has one, else `''` (present but
 * unmapped, shown as "Do not import" until the user picks a property).
 */
export function moveColumnBetweenTypes(
  columnMappings: ColumnMappings,
  suggestionByVirtualKey: Record<string, ColumnMapping>,
  sourceVirtualKey: string,
  targetVirtualKey: string,
  column: string,
  unassignedKey: string
): ColumnMappings {
  if (sourceVirtualKey === targetVirtualKey) {
    return columnMappings;
  }
  const next: ColumnMappings = { ...columnMappings };

  // Remove from the source section (the unassigned bucket has no stored map).
  if (sourceVirtualKey !== unassignedKey) {
    const { [column]: _movedOut, ...sourceMap } =
      columnMappings[sourceVirtualKey] ??
      suggestionByVirtualKey[sourceVirtualKey] ??
      {};
    next[sourceVirtualKey] = sourceMap;
  }

  // Add to the target section (dropping into the bucket means "mapped nowhere").
  if (targetVirtualKey !== unassignedKey) {
    const targetMap: ColumnMapping = {
      ...(columnMappings[targetVirtualKey] ??
        suggestionByVirtualKey[targetVirtualKey] ??
        {}),
    };
    if (!(column in targetMap)) {
      targetMap[column] =
        suggestionByVirtualKey[targetVirtualKey]?.[column] ?? '';
    }
    next[targetVirtualKey] = targetMap;
  }

  return next;
}

// ============================================================================
// Column mapping helpers
// ============================================================================

/**
 * Filters out stale entries for a file (by prefix and bare fileId), then merges
 * in new virtual type entries — used when accepting a multi-type suggestion.
 */
export function mergeColumnMappingsForFile(
  prev: ColumnMappings,
  prefix: string,
  fileIdStr: string,
  newEntries: ColumnMappings
): ColumnMappings {
  const next: ColumnMappings = {};
  for (const [key, value] of Object.entries(prev)) {
    if (!key.startsWith(prefix) && key !== fileIdStr) {
      next[key] = value;
    }
  }
  return { ...next, ...newEntries };
}

/**
 * Synthesizes a MultiTypeMappingSuggestion for a single-entity-type file so
 * that all mapping state is stored under editKeys ({fileId}_virtual_{type})
 * rather than the bare fileId.
 */
export function synthesizeSingleTypeSuggestion(
  entityTypeName: string,
  mappings: Record<string, string>,
  newProperties?: NewPropertySuggestion[],
  internalColumns?: string[]
): MultiTypeMappingSuggestion {
  return {
    entity_types: [
      {
        entity_type_name: entityTypeName,
        mappings,
        new_properties: newProperties ?? [],
      },
    ],
    associations: [],
    confidence: 1,
    is_multi_type: false,
    internal_columns: internalColumns ?? [],
  };
}

/**
 * Removes all entries whose key starts with prefix — used when clearing a
 * file's virtual type mappings.
 */
export function removeColumnMappingsByPrefix(
  prev: ColumnMappings,
  prefix: string
): ColumnMappings {
  const next: ColumnMappings = {};
  for (const [key, value] of Object.entries(prev)) {
    if (!key.startsWith(prefix)) {
      next[key] = value;
    }
  }
  return next;
}

// ============================================================================
// Association helpers
// ============================================================================

/**
 * Check if an association mapping has all required fields filled.
 * A complete mapping has valid file IDs (in the uploaded files set) and non-empty column names.
 */
export function isAssociationMappingComplete(
  mapping: AssociationMapping,
  validFileIds: Set<string | number>
): boolean {
  return (
    validFileIds.has(mapping.source_file_id) &&
    validFileIds.has(mapping.target_file_id) &&
    mapping.source_column !== '' &&
    mapping.target_column !== ''
  );
}

/**
 * Stage entity type names — the `target_entity_type_name` of a stage-typed
 * `CrmEntityAssociationMapping` (see that type's doc comment).
 */
export const STAGE_ENTITY_TYPE_NAMES: ReadonlySet<string> = new Set([
  'ahoy_deal_pipeline_stage',
  'ahoy_contact_lifecycle_stage',
  'ahoy_task_pipeline_stage',
]);

/**
 * Whether a `CrmEntityAssociationMapping` is a stage mapping (target is a stage
 * entity type) rather than a regular property-lookup association. Stage entries
 * carry `value_mappings` instead of `target_property` — see the type's doc
 * comment and `CsvAnalyzer._resolve_stage_names` on the backend.
 */
export function isStageAssociationMapping(
  mapping: CrmEntityAssociationMapping
): boolean {
  return STAGE_ENTITY_TYPE_NAMES.has(mapping.target_entity_type_name);
}

/**
 * Whether a file/section (identified by its mapping key — a plain file id or a
 * multi-type virtual editKey) has a stage column mapped, via either rail: the
 * legacy `stageMappings[key].column` or a stage-typed entry in
 * `crmEntityAssociationMappings`.
 */
function hasStageColumnMapped(
  key: string,
  stageMappings: StageMappings,
  crmEntityAssociationMappings: CrmEntityAssociationMapping[]
): boolean {
  return (
    Boolean(stageMappings[key]?.column) ||
    crmEntityAssociationMappings.some(
      m => String(m.source_file_id) === key && isStageAssociationMapping(m)
    )
  );
}

/**
 * Generate a virtual file ID string for an entity type within a multi-type import.
 */
export function generateVirtualFileId(
  fileId: string | number,
  entityTypeName: string
): string {
  return `${fileId}_${entityTypeName}`;
}

/**
 * Pick a CSV column to stand in as the join key for a within-file association.
 *
 * Multi-type virtual files all share the same CSV, so the executor matches them
 * by row index — the column value is nominal. We still must supply a non-empty
 * column (the serializer requires it), so we prefer a unique-identifier property
 * and fall back to the first mapped column. Returns undefined if the type has no
 * mapped columns (shouldn't happen for an emitted section).
 */
export function pickJoinColumn(
  effectiveMappings: ColumnMapping
): string | undefined {
  for (const identifierProp of UNIQUE_IDENTIFIER_PROPERTIES) {
    const match = Object.entries(effectiveMappings).find(
      ([, prop]) => prop === identifierProp
    );
    if (match) {
      return match[0];
    }
  }
  return Object.keys(effectiveMappings)[0];
}

// ============================================================================
// Mapping validation (pure function)
// ============================================================================

/**
 * Validate that all files have sufficient mappings to submit.
 *
 * Checks:
 * - Each file has at least one non-skip mapping (or stage mapping)
 * - Select-type new properties have at least one option configured
 * - All association mappings reference valid file IDs and have columns set
 */
/**
 * The entity types a multi-type file currently presents as sections: declared
 * types whose effective column map (user override ?? AI suggestion) has at least
 * one column parked in it.
 *
 * This is the single source of truth for "what types does this file have right
 * now". The renderer's section/pill split, the sidebar's single-vs-multi label,
 * validation, and association gating all derive from it — so adding a type or
 * emptying one (dragging its last column to Unassigned) stays consistent across
 * every surface. The declared `entity_types` array is the candidate universe
 * (incl. pills the user can add to); it must NOT be used directly as "the file's
 * types" because it never shrinks when a type is emptied.
 *
 * NOTE: a type whose columns are all unmapped ('') still counts here — the
 * section is visible and editable. Whether a type actually EMITS a virtual file
 * is a stricter question owned by `processMultiTypeFile`, which additionally
 * drops types with no non-skip/non-empty mapping.
 */
export function getRenderedEntityTypeNames(
  suggestion: MultiTypeMappingSuggestion,
  columnMappings: ColumnMappings,
  fileId: string | number
): string[] {
  const result: string[] = [];
  for (const entityType of suggestion.entity_types) {
    const editKey = `${fileId}_virtual_${entityType.entity_type_name}`;
    const effective = columnMappings[editKey] ?? entityType.mappings;
    if (Object.keys(effective).length > 0) {
      result.push(entityType.entity_type_name);
    }
  }
  return result;
}

/**
 * The declared entity types of a file that would actually EMIT a virtual file
 * at submit. Mirrors processMultiTypeFile's emit rule exactly — same
 * importableMappingEntries filter and stage test — so validation and UI
 * affordances agree with the payload builder on which sections exist. A type
 * is emittable when it has at least one importable column mapping (non-skip,
 * non-empty, non-internal) or a stage column mapped.
 */
export function getEmittableEntityTypeNames(
  fileId: string | number,
  suggestion: MultiTypeMappingSuggestion,
  columnMappings: ColumnMappings,
  stageMappings: StageMappings,
  crmEntityAssociationMappings: CrmEntityAssociationMapping[]
): Set<string> {
  return new Set(
    suggestion.entity_types
      .filter(entityType => {
        const virtualKey = `${fileId}_virtual_${entityType.entity_type_name}`;
        const mappings = columnMappings[virtualKey] ?? entityType.mappings;
        const hasColumnMapping =
          importableMappingEntries(mappings, suggestion.internal_columns)
            .length > 0;
        const hasStageMapped = hasStageColumnMapped(
          virtualKey,
          stageMappings,
          crmEntityAssociationMappings
        );
        return hasColumnMapping || hasStageMapped;
      })
      .map(entityType => entityType.entity_type_name)
  );
}

export function validateMappings(
  uploadedFiles: UploadedFileEntry[],
  columnMappings: ColumnMappings,
  associationMappings: AssociationMapping[],
  stageMappings: StageMappings,
  multiTypeSuggestions: Record<string | number, MultiTypeMappingSuggestion>,
  userNewProperties: Record<string, NewPropertySuggestion[]>,
  crmEntityAssociationMappings: CrmEntityAssociationMapping[] = []
): boolean {
  if (uploadedFiles.length === 0) {
    return false;
  }

  // A column put in "Link to record" mode but left unconfigured (empty target
  // type/property) would be rejected by the backend serializer on submit. Block
  // submission until each link is completed or switched off. Stage mappings are
  // exempt from the target_property requirement — they resolve via curated
  // value_mappings instead (see CrmEntityAssociationMapping's doc comment).
  const everyLinkComplete = crmEntityAssociationMappings.every(
    mapping =>
      Boolean(mapping.target_entity_type_name) &&
      (isStageAssociationMapping(mapping) || Boolean(mapping.target_property))
  );
  if (!everyLinkComplete) {
    return false;
  }

  // Build set of all valid file IDs (real + virtual) for association validation
  const allValidFileIds = new Set<string | number>();

  // Per-file validation
  for (const file of uploadedFiles) {
    const suggestion = multiTypeSuggestions[file.file_id];
    if (suggestion) {
      // Register every declared type's virtual ids as valid association targets
      // (a type stays a valid drop target even before it has columns).
      for (const entityType of suggestion.entity_types) {
        allValidFileIds.add(
          `${file.file_id}_virtual_${entityType.entity_type_name}`
        );
        // Also add backend-format ID so association mappings (which use
        // effectiveFiles IDs) pass validation.
        allValidFileIds.add(
          generateVirtualFileId(file.file_id, entityType.entity_type_name)
        );
      }
      // The declared types that would actually EMIT a virtual file at submit
      // (the exact mirror of processMultiTypeFile's emit rule) — a section it
      // would drop at submit cannot satisfy any gate here.
      const emittableTypeNames = getEmittableEntityTypeNames(
        file.file_id,
        suggestion,
        columnMappings,
        stageMappings,
        crmEntityAssociationMappings
      );

      // The CrossFileAssociations editor emits PLAIN physical file ids
      // (numeric and string forms). buildSubmitPayload translates a plain id
      // to the virtual id of the file's DECLARED type and drops the
      // association when that section never emits — so only register the
      // plain forms when the declared type's section would emit. Otherwise
      // validation would accept an association that submit silently drops.
      if (emittableTypeNames.has(file.entity_type_name)) {
        allValidFileIds.add(file.file_id);
        allValidFileIds.add(String(file.file_id));
      }

      // A file must emit at least one importable section, else it would import
      // nothing (validation would pass while the payload is empty).
      if (emittableTypeNames.size === 0) {
        return false;
      }
      const renderedTypeNames = new Set(
        getRenderedEntityTypeNames(suggestion, columnMappings, file.file_id)
      );

      // Validate only the rendered (visible) types — each must have its select
      // new-properties configured before submission.
      for (const entityType of suggestion.entity_types) {
        if (!renderedTypeNames.has(entityType.entity_type_name)) {
          continue;
        }
        const virtualKey = `${file.file_id}_virtual_${entityType.entity_type_name}`;
        const mappings = columnMappings[virtualKey] ?? entityType.mappings;
        const effectiveMappings = importableMappingEntries(
          mappings,
          suggestion.internal_columns
        );

        // Validate select-type new properties have at least one option.
        // Merge AI-suggested and user-created props; user-created takes priority.
        // Scoped per entity type so a 'contact' prop never suppresses a 'company'
        // prop with the same name.
        const aiNewProps = entityType.new_properties ?? [];
        const userPropsForType = Object.values(userNewProperties)
          .flat()
          .filter(p => p.entity_type_name === entityType.entity_type_name);
        const effectiveNewProps = new Map<string, NewPropertySuggestion>();
        for (const p of aiNewProps) {
          effectiveNewProps.set(p.property_name, p);
        }
        for (const p of userPropsForType) {
          effectiveNewProps.set(p.property_name, p);
        }
        // Only validate properties a current column actually maps to. An
        // unmapped new prop (e.g. after the user remaps a column away from a
        // suggested/user-created select property) is dropped from the payload
        // by processMultiTypeFile, so requiring options here would wrongly
        // block an otherwise valid submission.
        const mappedPropNames = new Set(
          effectiveMappings.map(([, value]) => value)
        );
        for (const prop of effectiveNewProps.values()) {
          if (
            mappedPropNames.has(prop.property_name) &&
            prop.field_type_name === 'select' &&
            (!prop.options || prop.options.length < 2)
          ) {
            return false;
          }
        }
      }
    } else {
      // Standard file: validate has at least one non-skip column mapping or stage mapping
      allValidFileIds.add(file.file_id);
      allValidFileIds.add(String(file.file_id));
      const fileIdStr = String(file.file_id);
      const mappings = columnMappings[fileIdStr] || {};
      const hasStageMapped = hasStageColumnMapped(
        fileIdStr,
        stageMappings,
        crmEntityAssociationMappings
      );
      const hasNonSkipMapping = Object.values(mappings).some(
        value => value !== SKIP_MARKER && value !== ''
      );
      if (!hasNonSkipMapping && !hasStageMapped) {
        return false;
      }

      // Validate select-type new properties have at least one option.
      // Merge AI-suggested and user-created props; user-created takes priority.
      const aiProps = file.new_properties ?? [];
      const userProps = userNewProperties[fileIdStr] ?? [];
      const effectiveProps = new Map<string, NewPropertySuggestion>();
      for (const p of aiProps) {
        effectiveProps.set(p.property_name, p);
      }
      for (const p of userProps) {
        effectiveProps.set(p.property_name, p);
      }
      // Only validate properties a current column actually maps to (see the
      // multi-type branch above) so remapping away from a select property does
      // not block submission.
      const mappedPropNames = new Set(
        Object.values(mappings).filter(
          value => value !== SKIP_MARKER && value !== ''
        )
      );
      for (const prop of effectiveProps.values()) {
        if (
          mappedPropNames.has(prop.property_name) &&
          prop.field_type_name === 'select' &&
          (!prop.options || prop.options.length < 2)
        ) {
          return false;
        }
      }
    }
  }

  // Check all association mappings are complete (empty array is valid)
  return associationMappings.every(mapping =>
    isAssociationMappingComplete(mapping, allValidFileIds)
  );
}

// ============================================================================
// Submit payload builder (pure function)
// ============================================================================

export interface SubmitPayload {
  name: string;
  uploaded_files: UploadedFileEntry[];
  column_mappings: ColumnMappings;
  association_mappings?: AssociationMapping[];
  stage_mappings?: StageMappings;
  crm_entity_association_mappings?: CrmEntityAssociationMapping[];
  fixed_value_mappings?: FixedValueMapping[];
  match_columns?: Record<string, string[]>;
}

/**
 * Build the API request payload for create/reanalyze from wizard state.
 *
 * Handles:
 * - Expanding multi-type files into virtual files
 * - Merging AI-suggested and user-edited new properties
 * - Generating within-file virtual associations
 * - Applying user association toggles
 * - Stripping skip markers and empty mappings
 * - Re-stamping per-section match columns to virtual file ids
 */
export function buildSubmitPayload(
  importName: string,
  uploadedFiles: UploadedFileEntry[],
  columnMappings: ColumnMappings,
  associationMappings: AssociationMapping[],
  stageMappings: StageMappings,
  crmEntityAssociationMappings: CrmEntityAssociationMapping[],
  multiTypeSuggestions: Record<string | number, MultiTypeMappingSuggestion>,
  associationSelections: AssociationTypeSelections,
  userNewProperties: Record<string, NewPropertySuggestion[]>,
  fixedValueMappings: FixedValueMapping[],
  associationTypes: AssociationType[],
  matchesSource: AssociationMatcher,
  matchesTarget: AssociationMatcher,
  matchColumns: Record<string, string[]> = {}
): SubmitPayload {
  const finalUploadedFiles: UploadedFileEntry[] = [];
  const finalColumnMappings: ColumnMappings = {};
  const finalAssociationMappings: AssociationMapping[] = [];
  const finalStageMappings: StageMappings = {};
  // Fixed values re-stamped from a multi-type section's editKey to its virtual
  // file id.
  const translatedFixedValues: FixedValueMapping[] = [];
  // CRM-entity ("Link to record") associations re-stamped from a section's
  // editKey to its virtual file id (mirrors translatedFixedValues).
  const translatedCrmEntityAssociations: CrmEntityAssociationMapping[] = [];
  // Per-column unique-match toggles re-stamped from a section's editKey to its
  // virtual file id (mirrors translatedFixedValues).
  const finalMatchColumns: Record<string, string[]> = {};

  // Process each uploaded file — all files are expanded to virtual files
  for (const file of uploadedFiles) {
    const suggestion = multiTypeSuggestions[file.file_id];
    if (!suggestion) {
      throw new Error(
        `buildSubmitPayload: no multiTypeSuggestions entry for file ${file.file_id} — Phase 0 should have synthesized one`
      );
    }
    processMultiTypeFile(
      file,
      suggestion,
      columnMappings,
      stageMappings,
      fixedValueMappings,
      associationSelections,
      userNewProperties,
      associationTypes,
      matchesSource,
      matchesTarget,
      crmEntityAssociationMappings,
      matchColumns,
      finalUploadedFiles,
      finalColumnMappings,
      finalStageMappings,
      translatedFixedValues,
      finalAssociationMappings,
      translatedCrmEntityAssociations,
      finalMatchColumns
    );
  }

  // Translate user-defined cross-file associations from plain physical ids to
  // virtual ids; drop any whose endpoint file emitted no virtual entry (would
  // 400 at the serializer). validateMappings mirrors this emit test and blocks
  // submission for such associations, so the drop here is defense-in-depth —
  // it should only fire if the two checks drift.
  const physicalToVirtual = new Map<string, string>();
  for (const file of uploadedFiles) {
    physicalToVirtual.set(
      String(file.file_id),
      generateVirtualFileId(file.file_id, file.entity_type_name)
    );
  }
  const emittedIds = new Set(finalUploadedFiles.map(f => String(f.file_id)));
  for (const mapping of associationMappings) {
    const source =
      physicalToVirtual.get(String(mapping.source_file_id)) ??
      String(mapping.source_file_id);
    const target =
      physicalToVirtual.get(String(mapping.target_file_id)) ??
      String(mapping.target_file_id);
    if (!emittedIds.has(source) || !emittedIds.has(target)) {
      logger.warn('Dropping cross-file association: endpoint not emitted', {
        sourceFileId: source,
        targetFileId: target,
      });
      continue;
    }
    finalAssociationMappings.push({
      ...mapping,
      source_file_id: source,
      target_file_id: target,
    });
  }

  const payload: SubmitPayload = {
    name: importName,
    uploaded_files: finalUploadedFiles,
    column_mappings: finalColumnMappings,
  };
  if (finalAssociationMappings.length > 0) {
    payload.association_mappings = finalAssociationMappings;
  }
  if (Object.keys(finalStageMappings).length > 0) {
    payload.stage_mappings = finalStageMappings;
  }
  // Drop incomplete "Link to record" mappings (no target type/property) — the
  // serializer rejects an empty target_entity_type_name. validateMappings already
  // blocks submit for these; this is a defensive net. Stage mappings are exempt
  // from the target_property requirement (see CrmEntityAssociationMapping's doc
  // comment) — they resolve via value_mappings instead.
  const completeCrmEntityAssociations = translatedCrmEntityAssociations.filter(
    mapping =>
      Boolean(mapping.target_entity_type_name) &&
      (isStageAssociationMapping(mapping) || Boolean(mapping.target_property))
  );
  if (completeCrmEntityAssociations.length > 0) {
    payload.crm_entity_association_mappings = completeCrmEntityAssociations;
  }
  // Per-file fixed value defaults (owner, note visibility). All files go through
  // processMultiTypeFile, which re-stamps editKey-sourced rows to virtual file ids.
  const completeFixedValues = translatedFixedValues;
  if (completeFixedValues.length > 0) {
    payload.fixed_value_mappings = completeFixedValues;
  }
  if (Object.keys(finalMatchColumns).length > 0) {
    payload.match_columns = finalMatchColumns;
  }
  return payload;
}

function processMultiTypeFile(
  file: UploadedFileEntry,
  suggestion: MultiTypeMappingSuggestion,
  columnMappings: ColumnMappings,
  stageMappings: StageMappings,
  fixedValueMappings: FixedValueMapping[],
  associationSelections: AssociationTypeSelections,
  userNewProperties: Record<string, NewPropertySuggestion[]>,
  associationTypes: AssociationType[],
  matchesSource: AssociationMatcher,
  matchesTarget: AssociationMatcher,
  crmEntityAssociationMappings: CrmEntityAssociationMapping[],
  matchColumns: Record<string, string[]>,
  outFiles: UploadedFileEntry[],
  outMappings: ColumnMappings,
  outStageMappings: StageMappings,
  outFixedValues: FixedValueMapping[],
  outAssociations: AssociationMapping[],
  outCrmEntityAssociations: CrmEntityAssociationMapping[],
  outMatchColumns: Record<string, string[]>
): void {
  // Emitted (non-empty) entity-type sections for this file, in section order.
  // A type with no mapped columns is just a pill in the UI and is skipped — so
  // associations can only reference types that actually became virtual files.
  const emittedTypes: Array<{
    entityTypeName: string;
    virtualFileId: string;
    joinColumn: string;
  }> = [];

  // Generate virtual files for each entity type
  for (const entityTypeMapping of suggestion.entity_types) {
    const virtualFileId = generateVirtualFileId(
      file.file_id,
      entityTypeMapping.entity_type_name
    );

    const editKey = `${file.file_id}_virtual_${entityTypeMapping.entity_type_name}`;
    const rawMappings = columnMappings[editKey] ?? entityTypeMapping.mappings;
    const effectiveMappings = Object.fromEntries(
      importableMappingEntries(rawMappings, suggestion.internal_columns)
    );

    // A type with no mapped columns AND no stage column must not produce a
    // virtual file. A stage-only section still emits so its stage is carried.
    const hasStageMapped = hasStageColumnMapped(
      editKey,
      stageMappings,
      crmEntityAssociationMappings
    );
    if (Object.keys(effectiveMappings).length === 0 && !hasStageMapped) {
      continue;
    }
    const joinColumn = pickJoinColumn(effectiveMappings);
    emittedTypes.push({
      entityTypeName: entityTypeMapping.entity_type_name,
      virtualFileId,
      joinColumn: joinColumn ?? '',
    });

    // Merge user-created and AI-suggested new properties for this entity type.
    // The dedup set is reset per entity type so a 'contact' property never
    // suppresses a 'company' property with the same property_name.
    const mappedProps = new Set(Object.values(effectiveMappings));
    const seenNewProps = new Set<string>();
    const referencedNewProps: NewPropertySuggestion[] = [];
    for (const props of Object.values(userNewProperties)) {
      for (const np of props) {
        if (
          np.entity_type_name === entityTypeMapping.entity_type_name &&
          mappedProps.has(np.property_name) &&
          !seenNewProps.has(np.property_name)
        ) {
          seenNewProps.add(np.property_name);
          referencedNewProps.push(np);
        }
      }
    }
    const finalNewProps = [
      ...(entityTypeMapping.new_properties ?? []).filter(
        np =>
          mappedProps.has(np.property_name) &&
          !seenNewProps.has(np.property_name)
      ),
      ...referencedNewProps,
    ];

    outFiles.push({
      file_id: virtualFileId,
      source_file_id:
        typeof file.file_id === 'number' ? file.file_id : undefined,
      filename: file.filename,
      entity_type_name: entityTypeMapping.entity_type_name,
      new_properties: finalNewProps,
      // Source-file-level: persist on every virtual file so resume can restore
      // the read-only "Internal columns" section (same value across a source's
      // virtual files).
      internal_columns: suggestion.internal_columns ?? [],
    });

    outMappings[virtualFileId] = effectiveMappings;

    // Carry any stage mapping configured for this virtual entity type
    const virtualStageMapping = stageMappings[editKey];
    if (virtualStageMapping) {
      outStageMappings[virtualFileId] = virtualStageMapping;
    }

    // Carry per-section defaults (owner / visibility / fixed associations). The
    // UI keys them by the section's editKey; the payload's virtual file uses
    // virtualFileId, so re-stamp source_file_id to match (mirrors stage/column).
    for (const mapping of fixedValueMappings) {
      if (String(mapping.source_file_id) === editKey) {
        outFixedValues.push({ ...mapping, source_file_id: virtualFileId });
      }
    }

    // Carry per-section "Link to record" (CRM entity) associations. Like fixed
    // values, these are keyed to a section, but the payload's virtual file uses
    // virtualFileId. Match the live-UI editKey ({id}_virtual_{type}) OR the
    // already-normalized virtualFileId ({id}_{type}, as a resumed import carries
    // it) and re-stamp source_file_id to virtualFileId so it matches the
    // uploaded_files entry the backend validates against.
    //
    // We check rawMappings (not effectiveMappings) because a live Link-mode
    // column is stored as SKIP_MARKER — it's a key in rawMappings but absent
    // from effectiveMappings. After a drag-out, moveColumnBetweenTypes removes
    // the column key entirely, so Object.hasOwn(rawMappings, …) becomes false
    // and correctly drops the stale link.
    for (const mapping of crmEntityAssociationMappings) {
      const src = String(mapping.source_file_id);
      if (
        (src === editKey || src === virtualFileId) &&
        Object.hasOwn(rawMappings, mapping.source_column)
      ) {
        outCrmEntityAssociations.push({
          ...mapping,
          source_file_id: virtualFileId,
        });
      }
    }

    // Carry per-section unique-match column toggles. Like fixed values and
    // CRM-entity associations, these are keyed to a section (editKey or, on
    // resume, the already-normalized virtualFileId), and re-stamped to
    // virtualFileId. Only columns that survived into effectiveMappings are
    // kept — the serializer requires every match column be a mapped column.
    const candidates =
      matchColumns[editKey] ?? matchColumns[virtualFileId] ?? [];
    const kept = candidates.filter(col =>
      Object.hasOwn(effectiveMappings, col)
    );
    if (kept.length > 0) {
      outMatchColumns[virtualFileId] = Array.from(new Set(kept));
    }
  }

  // Build within-file associations between the emitted sections. Direction
  // follows section order: the earlier (above) type is the source, the later
  // (below) type is the target. The association type is the user's selection,
  // defaulting to the first valid (kind-aware) type for the directed pair; a
  // pair the user disabled, or one with no valid association type, is skipped.
  const fileSelections = associationSelections[file.file_id] ?? {};
  for (let i = 0; i < emittedTypes.length; i++) {
    for (let j = i + 1; j < emittedTypes.length; j++) {
      const source = emittedTypes[i];
      const target = emittedTypes[j];
      const validTypes = validAssociationTypesForPair(
        associationTypes,
        matchesSource,
        matchesTarget,
        source.entityTypeName,
        target.entityTypeName
      );
      const chosenType = resolveSelectedAssociationType(
        fileSelections,
        source.entityTypeName,
        target.entityTypeName,
        validTypes
      );
      if (!chosenType || !source.joinColumn || !target.joinColumn) {
        continue;
      }
      outAssociations.push({
        source_file_id: source.virtualFileId,
        source_column: source.joinColumn,
        target_file_id: target.virtualFileId,
        target_column: target.joinColumn,
        association_type_name: chosenType,
      });
    }
  }
}

// ============================================================================
// Hydration helpers (pure functions)
// ============================================================================

export interface HydratedFileData {
  fileColumns: Record<string, string[]>;
  fileRowCounts: Record<string, number>;
  fileSampleRows: Record<string, Record<string, string>[]>;
  analysisErrorProperties: Record<string, Record<string, string>>;
}

/**
 * Extract file metadata from an analysis result for hydrating the mapping step.
 *
 * Pulls column headers, row counts, sample data, and validation error summaries
 * from the analysis result JSON. Falls back to column_mappings keys when no
 * analysis data is available.
 */
export function hydrateFileData(data: CsvImport): HydratedFileData {
  const cols: Record<string, string[]> = {};
  const counts: Record<string, number> = {};
  const samples: Record<string, Record<string, string>[]> = {};
  const errorProps: Record<string, Record<string, string>> = {};

  // Build a virtual-file-ID → source-file-ID mapping so analysis results
  // keyed by virtual ID (e.g. "22_ahoy_contact") can be re-keyed under
  // the source ID ("22") that the wizard's uploadedFiles state uses.
  const virtualToSource = new Map<string, string>();
  for (const file of data.uploaded_files) {
    const srcId = file.source_file_id;
    if (srcId !== undefined && String(srcId) !== String(file.file_id)) {
      virtualToSource.set(String(file.file_id), String(srcId));
    }
  }

  if (data.analysis_result) {
    const fileResults = getFileResults(data.analysis_result);
    for (const [rawFileId, fileData] of Object.entries(fileResults)) {
      // Re-key virtual file IDs to their source ID so the result matches
      // the collapsed uploadedFiles entries.
      const fileId = virtualToSource.get(rawFileId) ?? rawFileId;
      const rawRows = fileData.rows ?? [];
      if (rawRows.length > 0) {
        // Use first virtual file's columns for the source (all virtuals
        // share the same physical CSV columns).
        if (!cols[fileId]) {
          cols[fileId] = Object.keys(rawRows[0]);
        }
      } else if ((fileData.analyzed_rows?.length ?? 0) > 0) {
        const firstRawData = (fileData.analyzed_rows ?? []).find(
          row => row.raw_data && Object.keys(row.raw_data).length > 0
        )?.raw_data;
        if (firstRawData && !cols[fileId]) {
          cols[fileId] = Object.keys(firstRawData);
        }
      }
      // Sum row counts across virtual files sharing the same source
      counts[fileId] =
        (counts[fileId] ?? 0) + (fileData.row_count ?? rawRows.length);
      // Sample data for the mapping preview: pick the 5 most-populated rows
      // (not just the first 5) so the preview shows a meaningful example even
      // when the leading rows are sparse.
      const populatedCount = (r: Record<string, string>): number =>
        Object.values(r).reduce((n, v) => n + (v.trim() ? 1 : 0), 0);
      if (rawRows.length > 0) {
        const fileSamples = rawRows
          .map((row): Record<string, string> => {
            const r: Record<string, string> = {};
            for (const [k, v] of Object.entries(row)) {
              r[k] = v == null ? '' : String(v);
            }
            return r;
          })
          .sort((a, b) => populatedCount(b) - populatedCount(a))
          .slice(0, 5);
        if (!samples[fileId]) {
          samples[fileId] = fileSamples;
        }
      } else if ((fileData.analyzed_rows?.length ?? 0) > 0) {
        const fileSamples = (fileData.analyzed_rows ?? [])
          .filter(row => row.raw_data && Object.keys(row.raw_data).length > 0)
          .slice(0, 50)
          .map(row => {
            const r: Record<string, string> = {};
            for (const [k, v] of Object.entries(row.raw_data ?? {})) {
              r[k] = v == null ? '' : String(v);
            }
            return r;
          })
          .sort((a, b) => populatedCount(b) - populatedCount(a))
          .slice(0, 5);
        if (!samples[fileId]) {
          samples[fileId] = fileSamples;
        }
      }
      // Collect property names that caused validation errors and keep
      // one example error message per property for the Mapping step tooltip.
      const errMap: Record<string, string> = {};
      for (const row of fileData.analyzed_rows ?? []) {
        if (row.errors) {
          for (const e of row.errors) {
            if (e.property_name && !errMap[e.property_name]) {
              errMap[e.property_name] = e.error ?? '';
            }
          }
        }
      }
      if (Object.keys(errMap).length > 0) {
        // Merge error properties for virtual files sharing the same source
        errorProps[fileId] = { ...errorProps[fileId], ...errMap };
      }
    }
  }

  // Fall back: derive columns from column_mappings keys
  for (const file of data.uploaded_files) {
    const fid =
      virtualToSource.get(String(file.file_id)) ?? String(file.file_id);
    if (!cols[fid] && data.column_mappings[String(file.file_id)]) {
      cols[fid] = Object.keys(data.column_mappings[String(file.file_id)]);
    }
  }

  return {
    fileColumns: cols,
    fileRowCounts: counts,
    fileSampleRows: samples,
    analysisErrorProperties: errorProps,
  };
}

/**
 * Re-key a section-scoped mapping's `source_file_id` from the backend virtual
 * file id (`{sourceId}_{type}`) back to the live-UI editKey
 * (`{sourceId}_virtual_{type}`), so resumed state matches the keys the mapping
 * UI and preview read. Mirrors the editKey derivation used when synthesizing a
 * new import. A mapping whose `source_file_id` matches no uploaded file is left
 * untouched rather than dropped, preserving user data.
 */
function rekeySourceFileIdToEditKey<
  T extends { source_file_id: number | string },
>(mappings: T[], uploadedFiles: UploadedFileEntry[]): T[] {
  return mappings.map(mapping => {
    const file = uploadedFiles.find(
      f => String(f.file_id) === String(mapping.source_file_id)
    );
    if (!file) {
      return mapping;
    }
    const srcId = file.source_file_id;
    const isVirtual =
      srcId !== undefined && String(srcId) !== String(file.file_id);
    const keyOwner = isVirtual ? srcId : file.file_id;
    const editKey = `${keyOwner}_virtual_${file.entity_type_name}`;
    return { ...mapping, source_file_id: editKey };
  });
}

/**
 * Rebuild `multiTypeSuggestions` and re-keyed mappings from saved import data.
 *
 * Fixes the resume bug where multi-type imports lose their sections because
 * the backend stores virtual file IDs in `{id}_{type}` format while the UI
 * uses `{id}_virtual_{type}` editKeys.
 *
 * Virtual files (source_file_id !== file_id) are grouped by source_file_id and
 * their mappings are re-keyed to editKey format. Standard files (no
 * source_file_id, or source_file_id === file_id) — i.e. legacy pre-consolidation
 * payloads — each get a synthesized single-type suggestion so every uploaded
 * file has a suggestion entry. buildSubmitPayload requires this invariant (it
 * fail-fasts otherwise), so resuming a legacy standard-file import would throw
 * at submit without this synthesis.
 */
export function reconstructSuggestionsFromPayload(data: {
  uploaded_files: UploadedFileEntry[];
  column_mappings: ColumnMappings;
  stage_mappings?: StageMappings;
  fixed_value_mappings?: FixedValueMapping[];
  crm_entity_association_mappings?: CrmEntityAssociationMapping[];
  association_mappings?: AssociationMapping[];
  match_columns?: Record<string, string[]>;
}): {
  suggestions: Record<string | number, MultiTypeMappingSuggestion>;
  columnMappings: ColumnMappings;
  stageMappings: StageMappings;
  fixedValueMappings: FixedValueMapping[];
  crmEntityAssociationMappings: CrmEntityAssociationMapping[];
  associationSelections: AssociationTypeSelections;
  crossFileAssociationMappings: AssociationMapping[];
  matchColumns: Record<string, string[]>;
} {
  // Group virtual files (multi-type) by their source_file_id
  const virtualBySource = new Map<number, UploadedFileEntry[]>();
  for (const file of data.uploaded_files) {
    const srcId = file.source_file_id;
    if (srcId !== undefined && String(srcId) !== String(file.file_id)) {
      const group = virtualBySource.get(srcId) ?? [];
      group.push(file);
      virtualBySource.set(srcId, group);
    }
  }

  const suggestions: Record<string | number, MultiTypeMappingSuggestion> = {};
  const columnMappings: ColumnMappings = {};
  const stageMappings: StageMappings = {};
  const matchColumns: Record<string, string[]> = {};

  for (const [sourceFileId, files] of virtualBySource) {
    const entityTypes: EntityTypeMappingSuggestion[] = [];
    for (const file of files) {
      const backendId = generateVirtualFileId(
        sourceFileId,
        file.entity_type_name
      );
      const backendMappings = data.column_mappings[String(backendId)];
      // DEFENSIVE: skip entity types with no saved column_mappings entry
      if (!backendMappings) {
        continue;
      }
      entityTypes.push({
        entity_type_name: file.entity_type_name,
        mappings: backendMappings,
        new_properties: file.new_properties ?? [],
      });
      const editKey = `${sourceFileId}_virtual_${file.entity_type_name}`;
      columnMappings[editKey] = backendMappings;
      // Re-key stage mapping from backend id to editKey
      const stageEntry = data.stage_mappings?.[String(backendId)];
      if (stageEntry) {
        stageMappings[editKey] = stageEntry;
      }
      // Re-key match columns from backend id to editKey
      const matchEntry = data.match_columns?.[String(backendId)];
      if (matchEntry && matchEntry.length > 0) {
        matchColumns[editKey] = matchEntry;
      }
    }
    if (entityTypes.length === 0) {
      continue;
    }
    suggestions[sourceFileId] = {
      entity_types: entityTypes,
      associations: [],
      confidence: 1,
      is_multi_type: entityTypes.length > 1,
      // Source-file-level and identical across a source's virtual files, so read
      // it from any of them to restore the "Internal columns" section on resume.
      internal_columns:
        files.find(f => f.internal_columns)?.internal_columns ?? [],
    };
  }

  // Synthesize single-type suggestions for legacy standard files (no virtual
  // expansion). Without this, resuming a pre-consolidation standard-file import
  // leaves multiTypeSuggestions empty and buildSubmitPayload fail-fasts at
  // submit. Mappings are re-keyed to editKey format so all downstream state is
  // keyed consistently with synthesized new-import suggestions.
  for (const file of data.uploaded_files) {
    const srcId = file.source_file_id;
    const isVirtual =
      srcId !== undefined && String(srcId) !== String(file.file_id);
    if (isVirtual) {
      continue;
    }
    const backendMappings = data.column_mappings[String(file.file_id)];
    // DEFENSIVE: skip standard files with no saved column_mappings entry
    if (!backendMappings) {
      continue;
    }
    const editKey = `${file.file_id}_virtual_${file.entity_type_name}`;
    columnMappings[editKey] = backendMappings;
    const stageEntry = data.stage_mappings?.[String(file.file_id)];
    if (stageEntry) {
      stageMappings[editKey] = stageEntry;
    }
    const matchEntry = data.match_columns?.[String(file.file_id)];
    if (matchEntry && matchEntry.length > 0) {
      matchColumns[editKey] = matchEntry;
    }
    suggestions[file.file_id] = synthesizeSingleTypeSuggestion(
      file.entity_type_name,
      backendMappings,
      file.new_properties,
      file.internal_columns
    );
  }

  // Re-key fixed value mappings to editKey format. Both virtual and standard
  // files now flow through processMultiTypeFile, which matches per-section
  // defaults by editKey ({sourceId}_virtual_{type}).
  const fixedValueMappings = rekeySourceFileIdToEditKey(
    data.fixed_value_mappings ?? [],
    data.uploaded_files
  );

  // Re-key stage/link crm_entity_association_mappings to editKey format too.
  // The backend persists these with source_file_id in {id}_{type} form, but the
  // mapping UI and preview (findStageAssociationMapping / FileMappingSection)
  // match on the {id}_virtual_{type} editKey — without this, a resumed import's
  // stage column mapping is silently unmatched and the preview shows the wrong
  // (fallback) stage while the mapping UI drops the mapped column.
  const crmEntityAssociationMappings = rekeySourceFileIdToEditKey(
    data.crm_entity_association_mappings ?? [],
    data.uploaded_files
  );

  // Restore explicit association-type choices from saved within-file
  // associations. Only mappings carrying an explicit association_type_name are
  // restored — a default-inferred pair is re-defaulted on resume. Disabled pairs
  // (absent from the payload) are not distinguishable from never-configured ones
  // and re-default to on; this is acceptable resume fidelity.
  const fileByBackendId = new Map(
    data.uploaded_files.map(f => [String(f.file_id), f])
  );
  const associationSelections: AssociationTypeSelections = {};
  // Cross-file (user-defined) associations are collapsed back to plain
  // physical file ids so the editor and Key badge render on resume;
  // buildSubmitPayload re-derives virtual ids at submit. Within-file
  // associations are NOT carried here — they are regenerated from
  // associationSelections by processMultiTypeFile at submit time, so keeping
  // them in associationMappings too would emit each one twice (duplicate
  // association rows / backend validation failures on resume).
  const crossFileAssociationMappings: AssociationMapping[] = [];
  for (const mapping of data.association_mappings ?? []) {
    const sourceFile = fileByBackendId.get(String(mapping.source_file_id));
    const targetFile = fileByBackendId.get(String(mapping.target_file_id));
    if (!sourceFile || !targetFile) {
      // Can't classify; preserve verbatim rather than drop user data.
      crossFileAssociationMappings.push(mapping);
      continue;
    }
    const sourceReal = sourceFile.source_file_id ?? sourceFile.file_id;
    const targetReal = targetFile.source_file_id ?? targetFile.file_id;
    const isWithinFile = String(sourceReal) === String(targetReal);
    if (!isWithinFile) {
      crossFileAssociationMappings.push({
        ...mapping,
        source_file_id: String(sourceReal),
        target_file_id: String(targetReal),
      });
      continue;
    }
    // Within-file: restore the explicit association-type choice for its section
    // pair. Only mappings carrying an explicit association_type_name are
    // restored — a default-inferred pair is re-defaulted on resume. Disabled
    // pairs (absent from the payload) are not distinguishable from
    // never-configured ones and re-default to on; acceptable resume fidelity.
    if (!mapping.association_type_name) {
      continue;
    }
    const key = directedPairKey(
      sourceFile.entity_type_name,
      targetFile.entity_type_name
    );
    const fileSel = associationSelections[sourceReal] ?? {};
    fileSel[key] = mapping.association_type_name;
    associationSelections[sourceReal] = fileSel;
  }

  return {
    suggestions,
    columnMappings,
    stageMappings,
    fixedValueMappings,
    crmEntityAssociationMappings,
    associationSelections,
    crossFileAssociationMappings,
    matchColumns,
  };
}

/**
 * Collapse virtual uploaded-file entries back into their source files.
 *
 * The backend stores multi-type files as virtual entries
 * (`{file_id: "22_ahoy_contact", source_file_id: 22}`), but the wizard UI
 * expects one `uploadedFiles` entry per physical file, keyed by `source_file_id`.
 * `multiTypeSuggestions` is keyed the same way (by source file ID), so
 * `MappingStep` can look up `multiTypeSuggestions[file.file_id]`.
 *
 * Standard (non-virtual) files pass through unchanged.
 */
export function collapseVirtualFiles(
  files: UploadedFileEntry[]
): UploadedFileEntry[] {
  const sourceMap = new Map<number, UploadedFileEntry>();
  const result: UploadedFileEntry[] = [];

  for (const file of files) {
    const srcId = file.source_file_id;
    const isVirtual =
      srcId !== undefined && String(srcId) !== String(file.file_id);

    if (!isVirtual) {
      // Standard file — keep as-is
      result.push(file);
      continue;
    }

    // Virtual file — collapse into source entry
    const existing = sourceMap.get(srcId);
    if (existing) {
      // Merge new_properties from additional virtual files
      if (file.new_properties && file.new_properties.length > 0) {
        existing.new_properties = [
          ...(existing.new_properties ?? []),
          ...file.new_properties,
        ];
      }
    } else {
      const collapsed: UploadedFileEntry = {
        file_id: srcId,
        filename: file.filename,
        entity_type_name: file.entity_type_name,
        new_properties: file.new_properties ? [...file.new_properties] : [],
      };
      sourceMap.set(srcId, collapsed);
      result.push(collapsed);
    }
  }

  return result;
}

// ============================================================================
// Stage mapping helpers
// ============================================================================

/**
 * Count how many stage values are unmapped across all files.
 *
 * An empty mapping ('') is a DELIBERATE "No stage" choice — the row imports with
 * no stage and is exempt from the unmatched default — so it is NOT counted as
 * unmapped. Only a missing/nullish value would count, which the UI never
 * produces (every addressed value is a real stage or an explicit ''). Values the
 * user never addressed are simply absent from value_mappings and fall to the
 * configured default; they are not represented here.
 */
export function countUnmappedStageValues(stageMappings: StageMappings): number {
  let unmapped = 0;
  for (const entry of Object.values(stageMappings)) {
    if (entry.column && entry.value_mappings) {
      for (const mapped of Object.values(entry.value_mappings)) {
        if (mapped == null) {
          unmapped++;
        }
      }
    }
  }
  return unmapped;
}
